import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import {
  MapPin, Users, RefreshCw, ChevronDown, Clock, Zap,
  ClipboardCheck, Loader2, CheckCircle2, AlertCircle, Inbox,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import {
  ADMIN_HOTSPOT_REQUEST_STATS,
  ADMIN_HOTSPOT_REQUEST_CLUSTERS,
  ADMIN_HOTSPOT_REQUEST_LIST,
  ADMIN_HOTSPOT_REQUEST_SET_STATUS,
} from "../../api/services/hotspotRequestService";
import { getErrorMessage } from '@/utils/errorUtils';

type Cluster = {
  count: number;
  requested: number;
  planned: number;
  uniquePeople: number;
  latestAt: string;
  requestIds: string[];
  center: { lat: number; lng: number };
  cell: { lat: number; lng: number };
  labels: string[];
};

type RequestItem = {
  id: string;
  status: string;
  label: string;
  note: string;
  location: { lng: number; lat: number };
  user: { id: string; name: string; email: string; profileImage?: string } | null;
  createdAt: string;
};

type Stats = { total: number; requested: number; planned: number; live: number; declined: number };

const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "planned", label: "Planning" },
  { key: "live", label: "Live" },
  { key: "all", label: "All" },
];

// Leaflet pin colors by dominant status (hex — Leaflet can't read CSS vars)
const MARKER_COLOR: Record<string, string> = {
  planned: "#f59e0b",
  live: "#22c55e",
  requested: "#3b82f6",
};

const LEAFLET_CSS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
const LEAFLET_JS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";

const fmtTime = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const dominantStatus = (c: Cluster) => (c.planned > c.requested ? "planned" : "requested");

const clusterLabel = (c: Cluster) =>
  (c.labels || []).find((l) => l && l.trim()) ||
  `${c.center.lat.toFixed(4)}, ${c.center.lng.toFixed(4)}`;

// Load Leaflet from CDN once (avoids an npm dependency / build step)
let leafletPromise: Promise<unknown> | null = null;
const loadLeaflet = (): Promise<unknown> => {
  const w = window as unknown as Record<string, unknown>;
  if (w.L) return Promise.resolve(w.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve((window as unknown as { L: typeof import("leaflet") }).L));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve((window as unknown as { L: typeof import("leaflet") }).L);
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return leafletPromise;
};

export default function AdminHotspotRequests() {
  const { t } = useTranslation();
  const { theme } = useApp();
  const isDark = theme === "dark";

  const [stats, setStats] = useState<Stats | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; error?: boolean } | null>(null);

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<unknown>(null);
  const layerRef = useRef<unknown>(null);

  const showToast = (msg: string, error = false) => {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, l] = await Promise.all([
        ADMIN_HOTSPOT_REQUEST_STATS().catch(() => null),
        ADMIN_HOTSPOT_REQUEST_CLUSTERS({ status: statusFilter }),
        ADMIN_HOTSPOT_REQUEST_LIST({ status: statusFilter, limit: 100 }),
      ]);
      if (s?.success) setStats(s.data);
      if (c?.success) setClusters(c.data?.clusters || []);
      if (l?.success) setRequests(l.data || []);
    } catch (e: unknown) {
      console.error("load hotspot requests error", e);
      showToast("Failed to load coverage requests", true);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Init the Leaflet map once
  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapEl.current || mapRef.current) return;
        const Lfn = L as unknown as Record<string, (...args: unknown[]) => unknown>;
        const map = (Lfn.map as Function)(mapEl.current, { scrollWheelZoom: false });
        (map as Record<string, (...args: unknown[]) => unknown>).setView([6.5244, 3.3792], 11);
        const tile = (Lfn.tileLayer as Function)("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap contributors",
          maxZoom: 19,
        });
        (tile as Record<string, (...args: unknown[]) => unknown>).addTo(map);
        const lg = (Lfn.layerGroup as Function)();
        (lg as Record<string, (...args: unknown[]) => unknown>).addTo(map);
        layerRef.current = lg;
        mapRef.current = map;
        drawMarkers();
      })
      .catch(() => showToast("Map failed to load", true));
    return () => {
      cancelled = true;
      if (mapRef.current) {
        (mapRef.current as unknown as Record<string, (...args: unknown[]) => unknown>).remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw markers whenever clusters change
  const drawMarkers = useCallback(() => {
    const L = (window as unknown as { L: typeof import("leaflet") }).L;
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!L || !map || !layer) return;
    (layer as unknown as Record<string, (...args: unknown[]) => unknown>).clearLayers();
    if (!clusters.length) return;
    const pts: [number, number][] = [];
    clusters.forEach((c) => {
      const color = MARKER_COLOR[dominantStatus(c)];
      const radius = 9 + Math.sqrt(c.count) * 4;
      const m = L.circleMarker([c.center.lat, c.center.lng], {
        radius,
        color: "#ffffff",
        weight: 2,
        fillColor: color,
        fillOpacity: 0.85,
      });
      m.bindPopup(
        `<strong>${clusterLabel(c)}</strong><br/>${c.count} request${c.count === 1 ? "" : "s"} · ${c.uniquePeople} ${c.uniquePeople === 1 ? "person" : "people"}`
      );
      m.on("click", () => setExpanded(c.requestIds[0] || null));
      m.addTo(layer);
      pts.push([c.center.lat, c.center.lng]);
    });
    if (pts.length === 1) (map as unknown as Record<string, (...args: unknown[]) => unknown>).setView(pts[0], 13);
    else if (pts.length > 1) (map as unknown as Record<string, (...args: unknown[]) => unknown>).fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
  }, [clusters]);

  useEffect(() => {
    drawMarkers();
  }, [drawMarkers]);

  const setClusterStatus = async (c: Cluster, status: string) => {
    const key = c.requestIds[0] || clusterLabel(c);
    setUpdating(key);
    try {
      await Promise.all(c.requestIds.map((id) => ADMIN_HOTSPOT_REQUEST_SET_STATUS(id, status)));
      showToast(`Marked ${status === "planned" ? "in planning" : status}`);
      await load();
    } catch (e: unknown) {
      showToast(getErrorMessage(e) || "Failed to update", true);
    } finally {
      setUpdating(null);
    }
  };

  const cardBg = isDark ? "bg-[#1a1b1d] border-gray-800" : "bg-white border-gray-200";
  const textMain = isDark ? "text-white" : "text-gray-900";
  const textSub = isDark ? "text-gray-400" : "text-gray-600";
  const textMuted = isDark ? "text-gray-500" : "text-gray-400";

  const metric = (label: string, value: number | string) => (
    <div className={`rounded-xl p-4 ${isDark ? "bg-[#222]" : "bg-gray-50"}`}>
      <div className={`text-xs ${textSub}`}>{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${textMain}`}>{value}</div>
    </div>
  );

  const statusPill = (status: string) => {
    const map: Record<string, string> = {
      requested: isDark ? "bg-blue-900/40 text-blue-300" : "bg-blue-100 text-blue-700",
      planned: isDark ? "bg-amber-900/40 text-amber-300" : "bg-amber-100 text-amber-700",
      live: isDark ? "bg-green-900/40 text-green-300" : "bg-green-100 text-green-700",
    };
    const defaultLabel = status === "planned" ? "Planning" : status.charAt(0).toUpperCase() + status.slice(1);
    const label = t(`admin.hotspotRequests.tabs.${status}`, defaultLabel);
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${map[status] || (isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600")}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="relative">
      {toast && (
        <div
          className={`fixed top-6 right-6 z-[1000] px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium flex items-center gap-2 ${
            toast.error ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.error ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className={`text-lg font-semibold ${textMain}`}>{t('admin.hotspotRequests.title', 'Coverage demand')}</h2>
          <p className={`text-sm ${textSub}`}>{t('admin.hotspotRequests.subtitle', 'Where users are asking for hotspots — deploy where demand is highest.')}</p>
        </div>
        <button
          onClick={load}
          className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
         
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {metric(t('admin.hotspotRequests.metrics.total', 'Total requests'), stats?.total ?? 0)}
        {metric(t('admin.hotspotRequests.metrics.areas', 'Areas'), clusters.length)}
        {metric(t('admin.hotspotRequests.metrics.planning', 'In planning'), stats?.planned ?? 0)}
        {metric(t('admin.hotspotRequests.metrics.live', 'Live'), stats?.live ?? 0)}
      </div>

      {/* map */}
      <Card className={`${cardBg} p-0 overflow-hidden mb-5`}>
        <div ref={mapEl} style={{ height: 320, width: "100%" }} className="z-0" />
      </Card>

      {/* filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setStatusFilter(tab.key); setExpanded(null); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === tab.key
                ? "bg-blue-600 text-white"
                : isDark ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {t(`admin.hotspotRequests.tabs.${tab.key}`, tab.label)}
          </button>
        ))}
      </div>

      {/* ranked list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : clusters.length === 0 ? (
        <Card className={`${cardBg} flex flex-col items-center justify-center py-16 ${textMuted}`}>
          <Inbox className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm">{t('admin.hotspotRequests.empty', 'No coverage requests in this view yet.')}</p>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {clusters.map((c, idx) => {
            const key = c.requestIds[0] || `${c.cell.lat}-${c.cell.lng}-${idx}`;
            const isOpen = expanded === key || (c.requestIds[0] && expanded === c.requestIds[0]);
            const inCluster = requests.filter((r) => c.requestIds.includes(r.id));
            return (
              <Card key={key} className={`${cardBg} p-0 overflow-hidden`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : key)}
                  className={`w-full flex items-center justify-between gap-3 p-4 text-left ${isDark ? "hover:bg-gray-800/40" : "hover:bg-gray-50"}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium text-[15px] ${textMain}`}>
                        <MapPin className="w-4 h-4 inline-block -mt-0.5 mr-1 text-gray-400" />
                        {clusterLabel(c)}
                      </span>
                      {statusPill(dominantStatus(c))}
                    </div>
                    <div className={`text-xs mt-1 flex items-center gap-2 ${textMuted}`}>
                      <Users className="w-3 h-3" /> {c.uniquePeople} {c.uniquePeople === 1 ? "person" : "people"}
                      <Clock className="w-3 h-3 ml-1" /> {fmtTime(c.latestAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className={`text-xl font-semibold leading-none ${textMain}`}>{c.count}</div>
                      <div className={`text-[11px] ${textMuted}`}>request{c.count === 1 ? "" : "s"}</div>
                    </div>
                    <ChevronDown className={`w-4 h-4 ${textMuted} transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {isOpen && (
                  <div className={`border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                    {inCluster.length === 0 ? (
                      <p className={`text-xs px-4 py-3 ${textMuted}`}>Request details not on this page.</p>
                    ) : (
                      inCluster.map((r) => (
                        <div key={r.id} className={`flex items-center gap-3 px-4 py-2.5 border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${isDark ? "bg-blue-900/40 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
                            {(r.user?.name || "U").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm ${textMain}`}>{r.user?.name || "Unknown"}</div>
                            {r.note && <div className={`text-xs ${textSub}`}>{r.note}</div>}
                          </div>
                          <span className={`text-[11px] ${textMuted} whitespace-nowrap`}>{fmtTime(r.createdAt)}</span>
                        </div>
                      ))
                    )}
                    <div className={`flex gap-2 px-4 py-3 border-t ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                      <button
                        disabled={updating === (c.requestIds[0] || clusterLabel(c))}
                        onClick={() => setClusterStatus(c, "planned")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 flex items-center gap-1.5 ${
                          isDark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <ClipboardCheck className="w-3.5 h-3.5" /> {t('admin.hotspotRequests.actions.markPlanning', 'Mark planning')}
                      </button>
                      <button
                        disabled={updating === (c.requestIds[0] || clusterLabel(c))}
                        onClick={() => setClusterStatus(c, "live")}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white flex items-center gap-1.5"
                      >
                        {updating === (c.requestIds[0] || clusterLabel(c)) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                        {t('admin.hotspotRequests.actions.markLive', 'Mark live')}
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
